import React, { useEffect, useMemo, useRef } from 'react';

import { Affix, Card, Col, Flex, Row } from 'antd-v5';
import { isNil, orderBy } from 'lodash';
import { DateTime } from 'luxon';
import { MdArrowBack } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Heading, Select, Text } from 'scouting-ui/lib';

import type {
  CounselorMeritBadge,
  MeritBadge,
  MeritBadgeCounselorProfile,
} from '@appTypes/esb';
import { useMBDict } from '@dict/duck/hooks';
import { routeParamsSel } from '@location/duck/selectorsTyped';
import {
  filterMeritBadgeVersions,
  getLatestActiveVersion,
  getVersionStatusLabel,
} from '@modules/bulkEntry/duck/utils/versions';
import ErrorBoundary from '@modules/core/components/ErrorBoundary';
import { useGetMeritBadgeCounselorProfileQuery } from '@modules/meritBadgeCounselors/api';
import { routeMBCDashboard } from '@modules/meritBadgeCounselors/mbcDashboard/duck/actions';
import type { RouteMBCBulkEntry } from '@modules/meritBadgeCounselors/routes.types';
import { ConditionalRender, Img } from '@shared/components';
import PageContainer from '@shared/components/Layouts/PageContainer';
import { intl } from '@shared/localization';
import { toastService } from '@toasts';

import { buildPreselection } from '../duck/preselect';
import {
  filteredMBSel,
  hasSelectionSel,
  mbSelectionSel,
  selectedVersionSel,
} from '../duck/selectors';
import { bulkEntryActions } from '../duck/slice';
import type { MBVersion } from '../duck/types';
import { validateYouthEligibility } from '../duck/validations';
import { EmptyState, NoAvailableYouth } from './Messages';
import { RequirementsCardGuard } from './RequirementsCard';
import YouthSelection from './YouthSelection';

interface BulkMBCEntryPageProps {
  mbcProfile: MeritBadgeCounselorProfile;
  memberId: number | string;
  availableMeritBadges: Array<{
    counselorMB: CounselorMeritBadge;
    meritBadge: MeritBadge;
  }>;
  isLoading: boolean;
}

type MeritBadgeOption = BulkMBCEntryPageProps['availableMeritBadges'][number];

function MBSelectOption(props: { item: MeritBadgeOption; imgSize: number }) {
  const { imgSize = 32 } = props;
  if (!props.item || !props.item.meritBadge) return <div>Invalid</div>;

  return (
    <Flex align="center" gap="small">
      <Img height={imgSize} src={props.item.meritBadge.imageUrl100} />
      <Text>{props.item.meritBadge.name}</Text>
    </Flex>
  );
}

function BulkMBCEntryPage(props: BulkMBCEntryPageProps) {
  const { mbcProfile, memberId, availableMeritBadges = [], isLoading } = props;
  const dispatch = useDispatch();
  const selection = useSelector(mbSelectionSel);
  const hasSelection = useSelector(hasSelectionSel);
  const selectedVersion = useSelector(selectedVersionSel);

  const selectOptions = orderBy(
    availableMeritBadges.map(item => ({
      ...item,
      value: item.counselorMB.meritBadgeId,
      label: item.meritBadge?.name,
      title: item.meritBadge?.name,
    })),
    ['title'],
    ['asc'],
  );
  type SelectOption = (typeof selectOptions)[number];

  const versionOptions = useMemo(() => {
    if (!selection.mb?.versions) return [];

    const versions = filterMeritBadgeVersions(
      selection.mb?.versions,
    ) as typeof selection.mb.versions;

    return versions.map(v => {
      const { status, date } = getVersionStatusLabel(v);
      const luxonDate = DateTime.fromJSDate(date.toDate());
      return {
        ...v,
        value: v.versionId,
        label: (
          <FormattedMessage
            id={`bulkEntry.versionDropdown.${status}`}
            values={{
              date: luxonDate.toFormat('MMM yyyy'),
              hasDate: luxonDate.isValid,
              version: v.version,
            }}
          />
        ),
      };
    });
  }, [selection]);

  // Auto-select latest active version when MB changes
  useEffect(() => {
    if (selection.mb?.versions && !selectedVersion) {
      const latest = getLatestActiveVersion(selection.mb.versions);
      if (latest) {
        dispatch(bulkEntryActions.setSelectedVersion(latest));
      }
    }
  }, [dispatch, selection.mb?.versions, selectedVersion]);

  if (!mbcProfile) return null;

  const noSelectionMade = !hasSelection;
  const hasYouths = mbcProfile.youths.length > 0;

  return (
    <main>
      <Flex vertical gap="middle">
        <Row>
          <Col xs={24}>
            <Button
              type="link"
              onClick={() => dispatch(routeMBCDashboard())}
              icon={<MdArrowBack />}
            >
              <FormattedMessage
                id="shared.backTo"
                values={{
                  name: intl.formatMessage({ id: 'page.mbcDashboard' }),
                }}
              />
            </Button>
          </Col>
          <Col xs={24}>
            <Heading font="display-h4" level={1}>
              <FormattedMessage id="page.mbcBulkEntry.page.title" />
            </Heading>
          </Col>
        </Row>
        <Affix>
          <Card>
            <Row gutter={[16, 16]} align={'middle'}>
              <Col xs={24} md={{ flex: 'auto' }}>
                <Select
                  listHeight={500}
                  virtual={false}
                  onChange={(_item: number, option: SelectOption) =>
                    dispatch(
                      bulkEntryActions.setSelection({
                        counselorMB: option.counselorMB,
                        mb: option.meritBadge,
                      }),
                    )
                  }
                  value={selection.counselorMB?.meritBadgeId}
                  placeholder={
                    <FormattedMessage id="page.mbcBulkEntry.selectMB" />
                  }
                  labelRender={(item: { value: unknown }) => {
                    const mbID = item.value;
                    const option = selectOptions.find(
                      item => item.counselorMB.meritBadgeId === mbID,
                    );
                    if (!option) return 'Invalid';
                    return <MBSelectOption imgSize={22} item={option} />;
                  }}
                  optionRender={item => {
                    const data = item.data as (typeof selectOptions)[number];
                    return <MBSelectOption item={data} imgSize={32} />;
                  }}
                  loading={isLoading}
                  showSearch
                  optionFilterProp="title"
                  options={selectOptions}
                />
              </Col>
              <Col xs={24} md={{ flex: 'auto' }}>
                <Select
                  disabled={!hasSelection}
                  value={selectedVersion?.versionId}
                  placeholder={
                    <FormattedMessage id="page.mbcBulkEntry.selectVersion" />
                  }
                  onChange={(_value: string, option: MBVersion) => {
                    dispatch(bulkEntryActions.setSelectedVersion(option));
                  }}
                  options={versionOptions}
                />
              </Col>
            </Row>
          </Card>
        </Affix>
        <ConditionalRender value={noSelectionMade}>
          <EmptyState />
        </ConditionalRender>
        <ConditionalRender value={!noSelectionMade}>
          <Row gutter={[16, 16]}>
            <Col xs={{ order: 0, span: 24 }} sm={{ span: 10, order: 0 }}>
              <ConditionalRender value={!hasYouths}>
                <NoAvailableYouth />
              </ConditionalRender>
              <ConditionalRender value={hasYouths}>
                <YouthSelection youths={mbcProfile.youths} />
              </ConditionalRender>
            </Col>
            <Col xs={{ span: 24, order: 1 }} sm={{ span: 14, order: 1 }}>
              {selection.counselorMB && selection.mb && selectedVersion && (
                <RequirementsCardGuard
                  meritBadgeId={selection.counselorMB.meritBadgeId}
                  meritBadgeName={selection.mb.name}
                  meritBadgeImage={selection.mb.imageUrl100}
                  memberId={memberId}
                />
              )}
            </Col>
          </Row>
        </ConditionalRender>
      </Flex>
    </main>
  );
}

function BulkMBCEntryPageContainer() {
  const {
    counselorMemberId: memberId,
    meritBadgeId: preselectMBId,
    memberIds: preselectMemberIds,
  } = useSelector(routeParamsSel<RouteMBCBulkEntry>);
  const {
    meritBadgesById,
    response: { isLoading: isLoadingMBDict },
  } = useMBDict({ params: { status: 'all' } });
  const selection = useSelector(mbSelectionSel);
  const dispatch = useDispatch();
  const preselectApplied = useRef(false);

  const { data: mbcProfile, isLoading: isLoadingMbcProfile } =
    useGetMeritBadgeCounselorProfileQuery(
      { memberId },
      { skip: isNil(memberId) },
    );

  useEffect(() => {
    dispatch(bulkEntryActions.setMBCProfile({ mbcProfile }));
  }, [dispatch, mbcProfile]);

  useEffect(() => {
    if (preselectApplied.current || isNil(preselectMBId)) return;
    if (!mbcProfile || isLoadingMBDict) return;
    preselectApplied.current = true;

    const preselection = buildPreselection({
      meritBadgeId: preselectMBId,
      memberIds: preselectMemberIds ?? [],
      counselorMBs: mbcProfile.meritBadges,
      meritBadge: meritBadgesById.get(preselectMBId),
      youths: mbcProfile.youths,
    });

    if (!preselection) {
      toastService.warn(
        intl.formatMessage({ id: 'page.mbcBulkEntry.preselect.unavailable' }),
      );
      return;
    }

    const { skippedCount, ...selected } = preselection;
    dispatch(bulkEntryActions.preselect(selected));
    if (skippedCount > 0) {
      toastService.warn(
        intl.formatMessage(
          { id: 'page.mbcBulkEntry.preselect.skipped' },
          { count: skippedCount },
        ),
      );
    }
  }, [
    dispatch,
    isLoadingMBDict,
    mbcProfile,
    meritBadgesById,
    preselectMBId,
    preselectMemberIds,
  ]);

  const filteredMBs = useSelector(filteredMBSel);
  const selectedVersion = useSelector(selectedVersionSel);

  useEffect(() => {
    const youths = mbcProfile?.youths || [];
    if (!selection.counselorMB) return;

    const validations = youths
      .map(youth => {
        const result = validateYouthEligibility(
          youth,
          selection.counselorMB!,
          selectedVersion,
        );
        if (result.valid) return null;
        return { youth, errors: [result] };
      })
      .filter(item => item !== null);

    const validationsByMid = Object.fromEntries(
      validations.map(item => [item.youth.memberId, item.errors]),
    );

    dispatch(
      bulkEntryActions.setValidationResults({
        validationErrors: validationsByMid,
      }),
    );
  }, [dispatch, mbcProfile?.youths, selectedVersion, selection.counselorMB]);

  useEffect(
    () => () => {
      dispatch(bulkEntryActions.resetState());
    },
    [dispatch],
  );

  if (!mbcProfile) return null;

  const availableMeritBadges = filteredMBs
    .map(item => ({
      counselorMB: item,
      meritBadge: meritBadgesById.get(item.meritBadgeId)!,
    }))
    .filter(item => !!item.meritBadge);

  return (
    <BulkMBCEntryPage
      availableMeritBadges={availableMeritBadges}
      mbcProfile={mbcProfile}
      memberId={memberId}
      isLoading={isLoadingMBDict || isLoadingMbcProfile}
    />
  );
}

export default function BulkMBCEntryPageWrapper() {
  return (
    <ErrorBoundary>
      <PageContainer>
        <BulkMBCEntryPageContainer />
      </PageContainer>
    </ErrorBoundary>
  );
}
